import { useCallback, useEffect, useState } from "react";
import { Box } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useCaregiverAppGetShiftsQuery } from "@/setup/frontend/api/apiHooks";
import { buildPushNotificationsPermissionsQuery } from "@/notifications/hooks/buildPushNotificationsPermissionsQuery";
import { buildPushNotificationsRequestPermissionsMutation } from "@/notifications/hooks/buildPushNotificationsRequestPermissionsMutation";
import { buildPushNotificationsRegisterMutation } from "@/notifications/hooks/buildPushNotificationsRegisterMutation";
import { openAppSettings } from "@/caregiver-app/components/openAppSettings";
import { geolocationModule } from "@/caregiver-app/components/useGpsPermissionStatus";
import { getStableShiftsQueryAfterCursor } from "@/caregiver-app/utils";
import {
	queryMicrophonePermission,
	requestMicrophonePermission,
} from "@/caregiver-app/utils/microphone-permission";
import {
	OnboardingFlow,
	type OnboardingStep,
	type PermissionStatus,
} from "@/caregiver-app/components/onboarding/OnboardingFlow";
import { useOnboardingCompleted } from "@/caregiver-app/hooks/useOnboardingCompleted";
import { useSyncOfflineOnboarding } from "@/caregiver-app/hooks/useSyncOfflineOnboarding";
import { useLogger } from "@/logging/client/LoggerContext";
import { useTRPC } from "@/common/utils/trpc";

dayjs.extend(utc);

const deviceModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@capacitor/device")
		: null;

export default function CaregiverAppOnboardingPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const { isOnboardingEnabled, markCompleted, savePendingOnboarding } =
		useOnboardingCompleted();
	const onboardingSync = useSyncOfflineOnboarding();
	const logger = useLogger();

	const stepParam = searchParams.get("step") as OnboardingStep | null;
	const returnTo = searchParams.get("returnTo");

	const trpc = useTRPC();
	const [os, setOs] = useState<"ios" | "android">("ios");
	const [initialPermissions, setInitialPermissions] = useState<{
		notifications: PermissionStatus;
		location: PermissionStatus;
		microphone: PermissionStatus;
		sms: boolean;
	}>({
		notifications: "not-determined",
		location: "not-determined",
		microphone: "not-determined",
		sms: false,
	});

	const currentUserQuery = useQuery(trpc.users.currentUser.queryOptions());
	const shiftsQuery = useCaregiverAppGetShiftsQuery({
		after: getStableShiftsQueryAfterCursor(dayjs()),
	});

	const pushPermissionsQuery = useQuery(
		buildPushNotificationsPermissionsQuery(),
	);

	const registerPushNotificationsMutation = useMutation(
		buildPushNotificationsRegisterMutation(),
	);

	useEffect(() => {
		(async () => {
			if (!deviceModule) {
				setOs("ios");
				return;
			}
			const { Device } = await deviceModule;
			const info = await Device.getInfo();
			setOs(info.platform === "android" ? "android" : "ios");
		})();
	}, []);

	useEffect(() => {
		(async () => {
			let notifStatus: PermissionStatus = "not-determined";
			if (pushPermissionsQuery.data) {
				const receive = pushPermissionsQuery.data.receive;
				if (receive === "granted") notifStatus = "granted";
				else if (receive === "denied") notifStatus = "denied";
			}

			let locationStatus: PermissionStatus = "not-determined";
			if (geolocationModule) {
				try {
					const { Geolocation } = await geolocationModule;
					const result = await Geolocation.checkPermissions();
					if (result.location === "granted") locationStatus = "granted";
					else if (result.location === "denied") locationStatus = "denied";
				} catch {
					locationStatus = "not-determined";
				}
			}

			const microphoneStatus = await queryMicrophonePermission();

			const sms = currentUserQuery.data?.smsConsent?.consented ?? false;

			logger.info(
				`onboarding_initial_permissions notifications=${notifStatus} location=${locationStatus} microphone=${microphoneStatus}`,
			);
			setInitialPermissions({
				notifications: notifStatus,
				location: locationStatus,
				microphone: microphoneStatus,
				sms,
			});
		})();
	}, [pushPermissionsQuery.data, currentUserQuery.data]);

	const handleRequestNotificationPermission = useCallback(async () => {
		const mutation =
			buildPushNotificationsRequestPermissionsMutation(queryClient);
		try {
			if (!mutation.mutationFn) return "denied" as const;
			const result = await mutation.mutationFn();
			const status =
				result.receive === "granted" ? "granted" : ("denied" as const);
			logger.info(`onboarding_notification_permission result=${status}`);
			if (status === "granted") {
				await registerPushNotificationsMutation.mutateAsync(undefined, {
					onError: (e) => {
						logger.warn(`onboarding_notification_register error=${e}`);
					},
				});
			}
			return status;
		} catch {
			logger.warn("onboarding_notification_permission error");
			return "denied" as const;
		}
	}, [queryClient, logger, registerPushNotificationsMutation]);

	const handleRequestLocationPermission = useCallback(async () => {
		if (!geolocationModule) return "denied" as const;
		try {
			const { Geolocation } = await geolocationModule;
			const result = await Geolocation.requestPermissions();
			const status =
				result.location === "granted" ? "granted" : ("denied" as const);
			logger.info(`onboarding_location_permission result=${status}`);
			return status;
		} catch {
			logger.warn("onboarding_location_permission error");
			return "denied" as const;
		}
	}, [logger]);

	const handleRequestMicrophonePermission = useCallback(async () => {
		const status = await requestMicrophonePermission();
		logger.info(`onboarding_microphone_permission result=${status}`);
		return status;
	}, [logger]);

	const caregiverName = currentUserQuery.data?.info.first_name ?? "";

	const shifts = shiftsQuery.data?.pages?.flat() ?? [];
	const nextShift =
		shifts.find((s) => dayjs.utc(s.startTimestamp).isAfter(dayjs())) ?? null;

	const handleSmsConsent = useCallback(
		(consent: boolean) => {
			logger.info(`onboarding_sms_consent consent=${consent}`);
		},
		[logger],
	);

	const handleComplete = useCallback(
		async (permissions: {
			notifications: PermissionStatus;
			location: PermissionStatus;
			microphone: PermissionStatus;
			sms: boolean;
		}) => {
			logger.info(`onboarding_completed hasNextShift=${!!nextShift}`);

			const payload = {
				notificationsPermission: permissions.notifications,
				locationPermission: permissions.location,
				microphonePermission: permissions.microphone,
				smsConsent: permissions.sms,
			};

			// catch these errors to allow caregiver to proceed for at least this session
			await markCompleted().catch((e) => {
				logger.error(`onboarding markCompleted error ${e}`);
			});
			await savePendingOnboarding(payload).catch((e) => {
				logger.error(`onboarding savePendingOnboarding error ${e}`);
			});

			void onboardingSync.sync();

			if (returnTo) {
				const destination = new URL(returnTo, window.location.origin);
				if (permissions.microphone === "denied") {
					destination.searchParams.set("microphoneSkipped", "true");
				}
				navigate(`${destination.pathname}${destination.search}`, {
					replace: true,
				});
			} else if (nextShift) {
				navigate(`/app?startTimestamp=${nextShift.startTimestamp}`, {
					replace: true,
				});
			} else {
				navigate("/app", { replace: true });
			}
		},
		[
			markCompleted,
			savePendingOnboarding,
			navigate,
			nextShift,
			returnTo,
			logger,
			onboardingSync,
		],
	);
	const nextShiftProps = nextShift
		? {
				clientName: nextShift.clientName,
				date: dayjs.utc(nextShift.startTimestamp).local().format("ddd, MMM D"),
				startTime: dayjs.utc(nextShift.startTimestamp).local().format("h:mm A"),
				endTime: dayjs.utc(nextShift.endTimestamp).local().format("h:mm A"),
				durationHours: Number(nextShift.scheduledHours) || 0,
			}
		: null;

	if (!isOnboardingEnabled && !stepParam) {
		return <Navigate to="/app" replace />;
	}

	const initialStep: OnboardingStep = stepParam ?? "welcome";

	// When deep-linking to a specific permission step (e.g. from checkout),
	// override that permission's initial status to "not-determined" so the
	// OnboardingFlow effect does not immediately redirect to the "denied" step.
	const effectiveInitialPermissions = stepParam?.startsWith("microphone")
		? { ...initialPermissions, microphone: "not-determined" as const }
		: initialPermissions;

	return (
		<Box style={{ display: "flex", flexDirection: "column", flex: 1 }}>
			<OnboardingFlow
				caregiverName={caregiverName}
				os={os}
				onRequestNotificationPermission={handleRequestNotificationPermission}
				onRequestLocationPermission={handleRequestLocationPermission}
				onRequestMicrophonePermission={handleRequestMicrophonePermission}
				onSmsConsent={handleSmsConsent}
				onOpenAppSettings={() => {
					logger.info("onboarding_open_settings");
					void openAppSettings();
				}}
				onSkipNotifications={() => logger.info("onboarding_skip_notifications")}
				onSkipLocation={() => logger.info("onboarding_skip_location")}
				onSkipMicrophone={() => logger.info("onboarding_skip_microphone")}
				onComplete={handleComplete}
				nextShift={nextShiftProps}
				initialStep={initialStep}
				initialPermissions={effectiveInitialPermissions}
			/>
		</Box>
	);
}
